import type { ReactNode } from 'react';
import { create } from 'zustand';

import type { NotificationState } from 'components/shared/Notification';

export interface NotificationItem {
  id: string;
  state: NotificationState;
  title: string;
  // Custom glyph for the card's icon slot (notification toasts pass the inbox row's kind
  // chip); unset for AI-job cards, which render their lifecycle icons.
  icon?: ReactNode;
  timestamp?: string;
  actionSuccessText?: string;
  actionFailureText?: string;
  onActionClick?: () => void;
  onDismiss?: () => void;
  // When set, the toast auto-dismisses itself after this many ms via the provider's animated
  // slide-out (In-App Notifications toasts set 8000). AI-job cards leave it unset — they are
  // lifecycle-bound status trackers, never time-expired.
  autoDismissMs?: number;
}

type Actions = {
  addNotification: (notification: Omit<NotificationItem, 'id'> & { id?: string }) => string;
  removeNotification: (id: string) => void;
  updateNotification: (id: string, updates: Partial<Omit<NotificationItem, 'id'>>) => void;
};

type State = {
  notifications: NotificationItem[];
  notificationHeights: Record<string, number>;
  actions: Actions;
  setNotificationHeight: (id: string, height: number) => void;
};

export const useNotificationStore = create<State>((set) => ({
  notifications: [],
  notificationHeights: {},
  actions: {
    addNotification: (notification) => {
      const id = notification.id ?? `notification-${crypto.randomUUID()}`;
      set((state) => ({
        notifications: [{ ...notification, id }, ...state.notifications],
      }));
      return id;
    },
    removeNotification: (id) =>
      set((state) => {
        const newHeights = { ...state.notificationHeights };
        delete newHeights[id];
        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          notificationHeights: newHeights,
        };
      }),
    updateNotification: (id, updates) =>
      set((state) => ({
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, ...updates } : n)),
      })),
  },
  setNotificationHeight: (id, height) =>
    set((state) => ({
      notificationHeights: { ...state.notificationHeights, [id]: height },
    })),
}));

export const useNotificationActions = () => useNotificationStore((state) => state.actions);
